import PolarisIcon from '@/components/_common/PolarisIcon';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { useContext } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { Link, useLocation } from 'react-router-dom';
import Autocomplete from './Form/Autocomplete';
import { buildTitleURL, getTitlePath } from '@/_utils/titleUtils';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';

interface VersionSelectProps {
  options: number[];
  value: number | 'unspecified' | null;
  onChange(event: { target: { value: string } }): void;
  helperText?: string;
  error?: boolean;
  label?: string;
  disableClearable?: boolean;
  forceEnableOptions?: boolean;
  required?: boolean;
  sx?: any;
  id?: string;
  disabled?: boolean;
  highContrast?: boolean;
  showUnspecifiedVersionOption?: boolean;
}

export const UNSPECIFIED_VERSION = 'unspecified';
export const UNSPECIFIED_VERSION_LABEL = 'Unspecified Version';

const getTitleUrl = (isTitleV2: boolean, version: TitleEntity | TitleEntityV2) => {
  if (isTitleV2) {
    return buildTitleURL({
      path: getTitlePath(version),
      version: version.id
    });
  } else {
    return `/titles/${version.parent}/?tab=metadata&version_id=${version.id}`;
  }
};

// this is used as a custom component loaded via schema (title schema from profile)
// it is also used on step 2 of opv2 when viewing inventory
const VersionSelect = ({
  value,
  options,
  onChange,
  helperText,
  error,
  label,
  disableClearable = true,
  forceEnableOptions = false,
  required,
  sx,
  id,
  disabled,
  highContrast,
  showUnspecifiedVersionOption = false
}: VersionSelectProps) => {
  const location = useLocation();
  const versionKeys = useAppSelector(selectVersionKeys);

  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  // we can override the default options with the overrides in the config
  const versionDropdownOption = useContext(ConfigContext)?.orderPlacementConfig?.versionDropdownOption;
  const versionOptionLabel = useContext(ConfigContext)?.orderPlacementConfig?.versionOptionLabel;

  const renderFilteredOptions = (options: string[], params: any) => {
    const filtered = options.filter((option) => {
      const optionObj = versionKeys[option];
      const optionToString =
        `${optionObj?.name} ${optionObj.meta?.usage} ${optionObj?.meta?.friendly_id} ${optionObj?.meta?.piece_number} ${optionObj?.external_id}`.toLowerCase();
      return optionToString.indexOf(params.inputValue.toLowerCase()) !== -1;
    });

    if (showUnspecifiedVersionOption) {
      filtered.unshift(UNSPECIFIED_VERSION);
    }

    return filtered;
  };

  const renderOption = (props: any, option: any) => {
    const disabled = !forceEnableOptions && versionKeys?.[option]?.disabled;
    props.key = option;
    const parentId = versionKeys[option]?.parent;
    const showExtLink = option !== UNSPECIFIED_VERSION && !location.pathname.includes('/titles/');

    return (
      <StyledOption data-testid={`version-option-${option}`} container {...props} isdisabled={`${disabled}`}>
        {versionDropdownOption && option !== UNSPECIFIED_VERSION ? (
          versionDropdownOption(versionKeys[option], isTitleV2)
        ) : (
          <>
            <span style={{ wordBreak: 'normal' }}>{option === UNSPECIFIED_VERSION ? UNSPECIFIED_VERSION_LABEL : versionKeys[option]?.name}</span>
            {showExtLink && parentId && (
              <StyledLink to={getTitleUrl(isTitleV2, versionKeys[option])} target="_blank">
                <PolarisIcon icon="external_link" height={18} />
              </StyledLink>
            )}
          </>
        )}
      </StyledOption>
    );
  };

  return (
    <StyledAutocomplete
      data-testid={`version-select-${label?.replaceAll(/\s+/g, '-')?.toLowerCase()}`}
      label={label}
      options={options}
      value={value ?? null}
      id={id}
      onChange={(_, value: string) => {
        const disabled = !forceEnableOptions && versionKeys?.[value]?.disabled;
        if (disabled) return null;
        onChange({ target: { value } });
      }}
      disableClearable={disableClearable}
      handleHomeEndKeys
      clearOnBlur
      blurOnSelect
      helperText={helperText}
      error={error}
      inputProps={{ InputLabelProps: { required: error } }}
      filterOptions={renderFilteredOptions}
      isOptionEqualToValue={(option: any, value: any) => String(option) === String(value)}
      getOptionLabel={(option: any) => {
        if (option === UNSPECIFIED_VERSION) {
          return UNSPECIFIED_VERSION_LABEL;
        }
        if (!versionKeys[option]) return '';
        if (option.inputValue) return option.inputValue;
        return versionOptionLabel ? versionOptionLabel(versionKeys[option]) : versionKeys[option].name;
      }}
      renderOption={renderOption}
      required={required}
      sx={sx}
      disabled={disabled}
      contrast={highContrast ? 'high' : 'low'}
    />
  );
};

export default VersionSelect;

const StyledOption = styled(Grid)<{ isdisabled: string }>`
  &.MuiAutocomplete-option {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
    opacity: ${({ isdisabled }) => (isdisabled === 'true' ? 0.3 : 1)};
    cursor: ${({ isdisabled }) => (isdisabled === 'true' ? 'default' : 'pointer')};
    & svg {
      opacity: 0;
      min-width: 18px;
      color: ${({ theme }) => theme.palette.text.secondary};
    }
  }
  &.MuiAutocomplete-option:hover {
    & svg {
      opacity: 1;
    }
  }
`;

const StyledAutocomplete = styled(Autocomplete)<{ contrast: 'high' | 'low' }>`
  & .MuiAutocomplete-endAdornment {
    display: flex;
    flex-direction: row;
    column-gap: 3px;

    .MuiSvgIcon-root {
      z-index: 99;
    }

    .MuiAutocomplete-clearIndicator {
      .MuiTouchRipple-root {
        ${({ contrast, theme }) => {
          if (contrast === 'high') {
            return `
              background-color: ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.main + '8a'};
              opacity: 0.5;
            `;
          }
        }};
      }

      &:hover {
        .MuiTouchRipple-root {
          ${({ contrast, theme }) => {
            if (contrast === 'high') {
              return `
                background-color: ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.main + '8c'};
                opacity: 1;
              `;
            }
          }};
        }
      }
    }
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`;
